// src/components/layout/Navbar/Navbar.tsx
import React, { memo, useState, useEffect } from "react";
import {
  Box,
  Drawer,
  IconButton,
  Stack,
  useMediaQuery,
  Divider,
} from "@mui/material";
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme as useMuiTheme } from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { useTheme } from "../../../core/providers/ThemeProvider";
import { colors, shadows } from "../../styles/theme/colors";
import { containerMaxWidths, responsiveSpacing } from "../../styles/theme/theme";
import { responsivePadding } from "../../utils/responsive";
import { navLinks } from "../../data/navigation";
import CustomButton from "../ui/Button/Custombutton";
import { Logo } from "../common/Logo/Logo";
import translations from "../../translations/en.json";
import { getLogger } from "../../../shared/utils/logger";
import { useUpdateUserRoleMutation } from "../../../api/userApi2";
import { useUserStore } from "../../../app/store/useUserStore";
import { getRoleIdFromDefaultRoles, UserRole } from "../../../types/user";


export const Navbar: React.FC = memo(() => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const { userType, theme } = useTheme();
  const { isAuthenticated, loginWithRedirect, logout } = useAuth0();
  const isSmallScreen = useMediaQuery(muiTheme.breakpoints.down('lg'));
  const { user, updateUserRole: updateUserRoleInStore, defaultRoles } = useUserStore();
  const updateUserRoleMutation = useUpdateUserRoleMutation();

  const alternateUserType = userType === "sender" ? "traveller" : "Sender";
  const alternateRoute = userType === "sender" ? "/traveller" : "/sender";

  // Only check if we're on landing/home pages (/, /sender, /traveller, /generic)
  const isOnLandingPage = location.pathname === '/' ||
                          location.pathname === '/sender' ||
                          location.pathname === '/traveller' ||
                          location.pathname === '/generic';

  useEffect(() => {
    // Set initial scroll state
    setIsScrolled(window.scrollY > 10);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavigation = (path: string) => {
    if (path.startsWith('http')) {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      setIsMobileMenuOpen(false);
      setTimeout(() => {
        navigate(path);
      }, 0);
      return;
    }
    setIsMobileMenuOpen(false);
  };

  const handleRoleToggle = async () => {
    // For guest users, just navigate to the alternate route
    if (!isAuthenticated || !user?.user_id) {
      navigate(alternateRoute);
      setIsMobileMenuOpen(false);
      return;
    }

    try {
      // Determine the new role based on current userType
      const newRole = userType === "sender" ? UserRole.TRAVELLER : UserRole.SENDER;
      const newRoleId = getRoleIdFromDefaultRoles(newRole, defaultRoles);
      // Call the API to update the role
      await updateUserRoleMutation.mutateAsync({
        user_id: user.user_id,
        role_id: newRoleId,
      });

      // Update the local store
      updateUserRoleInStore(newRole);

      // Navigate to the new role's page
      navigate(alternateRoute);
      setIsMobileMenuOpen(false);

    } catch {
      // You might want to show a toast/notification here
    }
  };

  const handleSignup = () => {
    loginWithRedirect({
      screen_hint: "signup",
      appState: { returnTo: window.location.origin },
    }).catch((err) => {
    });
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  // Background opacity changes on scroll
  // Only use glassmorphic effect on landing pages when not scrolled
  const shouldUseSolidStyle = !isOnLandingPage || isScrolled;

  const navBackground = shouldUseSolidStyle
    ? colors.glass.white95
    : colors.glass.white30;

  // Logo variant changes based on scroll or page background
  const logoVariant = shouldUseSolidStyle ? 'dark' : 'light';

  // Text color changes based on scroll or page background
  const textColor = shouldUseSolidStyle ? colors.form.label : colors.neutral.white;

  return (
    <>
      {/* Desktop & Tablet Navigation */}
      <Box
        component="nav"
        sx={{
          position: 'relative',
          display: 'flex',
          width: '100%',
          maxWidth: `${containerMaxWidths.header}px`,
          height: { xs: 'auto', lg: '67.23px' },
          padding: {
            xs: '0.75rem 1rem',
            sm: '0.75rem 1.125rem',
            lg: '0.75rem 1.125rem',
            xl: '0.75rem 1.25rem',
            xxl: '0.75rem 1.5rem',
          },
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: {
            xs: responsiveSpacing.smallGap.xs,
            sm: responsiveSpacing.mediumGap.sm,
            lg: responsiveSpacing.mediumGap.lg,
            xl: responsiveSpacing.mediumGap.xl,
            xxl: responsiveSpacing.mediumGap.xxl,
          },
          background: navBackground,
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: '16px',
          transition: 'background 0.3s ease-in-out, box-shadow 0.3s ease-in-out, backdrop-filter 0.3s ease-in-out',
          boxShadow: shouldUseSolidStyle ? shadows.navbar : 'none',
          pointerEvents: 'auto',
          isolation: 'isolate',
        }}
      >
        {/* Navigation Links - Desktop Only */}
        {!isSmallScreen && (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: 'center',
              flex: '0 0 auto',
              minWidth: 'fit-content',
            }}
          >
            {navLinks.map((link) => (
              <CustomButton
                key={link.id}
                onClick={() => handleNavigation(link.href)}
                buttonText={link.label}
                sx={{
                  px: { lg: 1.25, xl: 1.5, xxl: 1.75 },
                  py: 0.5,
                  color: textColor,
                  fontSize: { lg: '1rem', xl: '1.0625rem', xxl: '1.125rem' },
                  fontWeight: 500,
                  fontFamily: theme.typography.bodyText.fontFamily,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: shouldUseSolidStyle
                      ? 'rgba(0, 0, 0, 0.05)'
                      : colors.glass.white10,
                    borderRadius: '8px',
                  },
                  transition: 'all 0.2s',
                  minWidth: 'auto',
                  boxShadow: 'none',
                  '&.MuiButton-root': {
                    boxShadow: 'none',
                  },
                }}
              />
            ))}
          </Stack>
        )}

        {/* Logo - Centered horizontally across the entire viewport */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: { xs: 1, lg: '1 1 auto' },
            order: { xs: 0, lg: 0 },
            position: { lg: 'absolute' },
            left: { lg: '50%' },
            transform: { lg: 'translateX(-50%)' },
            pointerEvents: { lg: 'auto' },
          }}
          onClick={() => handleNavigation('/')}
        >
          <Logo
            variant={logoVariant}
            sx={{
              width: { xs: '100px', sm: '110px', lg: '124.09px', xl: '135px', xxl: '145px' },
              height: { xs: '29px', sm: '32px', lg: '36px', xl: '39px', xxl: '42px' },
              cursor: 'pointer',
            }}
          />
        </Box>

        {/* CTA Buttons - Desktop Only */}
        {!isSmallScreen && (
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              alignItems: 'center',
              flex: '0 0 auto',
              minWidth: 'fit-content',
            }}
          >
            {/* Hide role toggle for generic users */}
            {userType !== 'generic' && (
              <CustomButton
                onClick={handleRoleToggle}
                buttonText={`${translations.common.RoleToggle} ${alternateUserType}`}
                disabled={isAuthenticated && updateUserRoleMutation.isPending}
                sx={{
                  color: textColor,
                  fontSize: { lg: '1rem', xl: '1.0625rem', xxl: '1.125rem' },
                  fontWeight: 500,
                  fontFamily: theme.typography.bodyText.fontFamily,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  px: { lg: 1, xl: 1.25, xxl: 1.5 },
                  backgroundColor: 'transparent',
                  '&:hover': {
                    textDecoration: 'underline',
                    backgroundColor: 'transparent',
                  },
                  minWidth: 'auto',
                  boxShadow: 'none',
                  '&.MuiButton-root': {
                    boxShadow: 'none',
                  },
                  '&.Mui-disabled': {
                    color: textColor,
                    opacity: 0.5,
                  },
                }}
              />
            )}

            <CustomButton
              onClick={handleSignup}
              buttonText={userType === 'generic' ? translations.navigation.getStarted : translations.navigation.signup}
              sx={{
                minWidth: { lg: '100px', xl: '110px', xxl: '120px' },
                height: { lg: '40px', xl: '42px', xxl: '44px' },
                px: { lg: 2, xl: 2.5, xxl: 3 },
                backgroundColor: colors.button.primary,
                color: colors.neutral.white,
                fontSize: { lg: '0.875rem', xl: '0.9375rem', xxl: '1rem' },
                fontWeight: 500,
                fontFamily: theme.typography.bodyText.fontFamily,
                textTransform: 'none',
                borderRadius: '8px',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: colors.button.primaryHover,
                },
                transition: 'background-color 0.2s',
              }}
            />
          </Stack>
        )}

        {/* Mobile Menu Button */}
        {isSmallScreen && (
          <IconButton
            onClick={toggleMobileMenu}
            sx={{
              p: 0.5,
              color: textColor,
              '&:hover': {
                backgroundColor: shouldUseSolidStyle
                  ? 'rgba(0, 0, 0, 0.05)'
                  : colors.glass.white10,
              },
            }}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <CloseIcon sx={{ width: 24, height: 24 }} />
            ) : (
              <MenuIcon sx={{ width: 24, height: 24 }} />
            )}
          </IconButton>
        )}
      </Box>

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="top"
        open={isMobileMenuOpen && isSmallScreen}
        onClose={toggleMobileMenu}
        PaperProps={{
          style: {
            marginTop: isSmallScreen ? '80px' : '90px',
            maxHeight: isSmallScreen ? 'calc(100vh - 80px)' : 'calc(100vh - 90px)',
            overflowY: 'auto',
          },
        }}
        sx={{
          '& .MuiDrawer-paper': {
            display: 'flex',
            flexDirection: 'column',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            background: colors.glass.white95,
            backdropFilter: 'blur(20px)',
            borderRadius: '0 0 16px 16px',
            padding: {
              xs: responsivePadding.container.mobile,
              sm: responsivePadding.container.tablet,
            },
          },
        }}
      >
        <Stack spacing={2}>
          {navLinks.map((link) => (
            <CustomButton
              key={link.id}
              onClick={() => handleNavigation(link.href)}
              buttonText={link.label}
              fullWidth
              sx={{
                justifyContent: 'flex-start',
                px: 2,
                py: 1,
                color: theme.colors.text,
                fontSize: '1rem',
                fontWeight: 500,
                fontFamily: theme.typography.bodyText.fontFamily,
                textTransform: 'none',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  borderRadius: '8px',
                },
                boxShadow: 'none',
                '&.MuiButton-root': {
                  boxShadow: 'none',
                },
              }}
            />
          ))}

          <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.1)', my: 1 }} />

          {/* Hide role toggle for generic users */}
          {userType !== 'generic' && (
            <CustomButton
              onClick={handleRoleToggle}
              buttonText={`${translations.common.RoleToggle} ${alternateUserType}`}
              disabled={isAuthenticated && updateUserRoleMutation.isPending}
              fullWidth
              sx={{
                justifyContent: 'flex-start',
                px: 2,
                py: 1,
                color: theme.colors.text,
                fontSize: '1rem',
                fontWeight: 500,
                fontFamily: theme.typography.bodyText.fontFamily,
                textTransform: 'none',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  borderRadius: '8px',
                },
                boxShadow: 'none',
                '&.MuiButton-root': {
                  boxShadow: 'none',
                },
                '&.Mui-disabled': {
                  color: theme.colors.text,
                  opacity: 0.5,
                },
              }}
            />
          )}

          {/* Fixed: Use Auth0 signup in mobile menu too */}
          {!isAuthenticated && (
            <CustomButton
              onClick={handleSignup}
              buttonText={userType === 'generic' ? translations.navigation.getStarted : translations.navigation.signup}
              fullWidth
              sx={{
                py: 1.5,
                backgroundColor: theme.colors.primary,
                color: colors.neutral.white,
                fontSize: '0.875rem',
                fontWeight: 500,
                fontFamily: theme.typography.bodyText.fontFamily,
                textTransform: 'none',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: colors.button.primaryHover,
                },
              }}
            />
          )}
        </Stack>
      </Drawer>
    </>
  );
});

Navbar.displayName = "Navbar";